import { useEffect, useState, type JSX } from 'react';

import { fetchEvent } from 'app-migration/api/api';
import KalshiLogo from 'components/DesignSystem/Icons/adhoc/KalshiLogo';
import FaceOffRow from 'components/ExternalWidgets/FaceOffRow';
import SquareSeriesImage from 'components/Image/SquareSeriesImage';
import Palette from 'DesignSystem2/Theme/Palette';
import { Palette_DarkMode } from 'DesignSystem2/Theme/Palette/PaletteVariations';
import Headline from 'DesignSystem3/Text/Headline';
import type { MarketVo } from 'models';
import { marketLastPriceCents, marketLastPriceChangeCents } from 'models/market';

interface MediumVariantAProps {
    markets: MarketVo[];
    eventName: string;
    eventTicker: string;
    seriesTicker: string;
    filterIds?: string[] | string;
    goToMarket: () => void;
    darkMode?: boolean;
    /** Embed mode: drop the card border so the embedder owns the chrome (legacy embed keeps it). */
    flexible?: boolean;
}

const MediumVariantA = ({
    markets,
    eventName,
    seriesTicker,
    eventTicker,
    filterIds,
    goToMarket,
    darkMode = false,
    flexible = false,
}: MediumVariantAProps): JSX.Element => {
    const [marketData, setMarketData] = useState(markets);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchEvent(eventTicker).then((event) => {
                if (event) {
                    if (filterIds) {
                        event.markets = event.markets.filter((m) => filterIds.includes(m.ticker_name));
                    }
                    setMarketData(event.markets.sort((a, b) => marketLastPriceCents(b) - marketLastPriceCents(a)));
                }
            });
        }, 30000);

        return () => clearInterval(interval);
    }, [eventTicker, filterIds]);

    return (
        <div
            className={`flex flex-col w-full gap-3 p-2 cursor-pointer text-text-x10${flexible ? '' : ' border border-solid rounded-x20 border-fill-x40'}`}
            style={{
                background: darkMode ? Palette_DarkMode.Surface.x10 : Palette.Surface.x10,
            }}
            onClick={goToMarket}
        >
            {/* Header row: image, title, logo */}
            <div className="flex items-center w-full gap-2">
                <SquareSeriesImage seriesTicker={seriesTicker} eventTicker={eventTicker} />
                <div className="flex-grow">
                    <Headline.x10 style={{ color: darkMode ? Palette_DarkMode.Text.x10 : Palette.Text.x10 }}>
                        {eventName}
                    </Headline.x10>
                </div>
                <KalshiLogo color={Palette.Brand.Primary} height="22px" />
            </div>
            {/* Markets below in column format */}
            <div className="flex flex-col w-full gap-2 p-2">
                {markets.map((market, index) => {
                    const data = marketData.find((m) => m.ticker_name === market.ticker_name) || market;
                    return (
                        <FaceOffRow
                            key={index}
                            rulebook_variables={market.rulebook_variables}
                            yes_sub_title={market.yes_sub_title}
                            odds={marketLastPriceCents(data)}
                            change={marketLastPriceChangeCents(data)}
                            largerText={true}
                            textColor={darkMode ? Palette_DarkMode.Text.x10 : Palette.Text.x10}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default MediumVariantA;
